import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react'
import axios from 'axios'
const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL

const REFRESH_INTERVAL_MS = 10 * 60 * 1000 // 10 minutes

interface User {
  token: string
  refreshToken: string
  email: string
  role: string
}

interface AuthContextType {
  user: User | null
  login: (userData: User) => void
  logout: () => void
  refreshToken: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const storedUser = localStorage.getItem('user')
  const [user, setUser] = useState<User | null>(storedUser ? JSON.parse(storedUser) : null)
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const logout = useCallback(() => {
    localStorage.removeItem('user')
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    setUser(null)
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
    window.location.hash = '#/login'
  }, [])

  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const storedUser = localStorage.getItem('user')
      if (!storedUser) {
        logout()
        return false
      }
      const refreshEndpoint = `${BASE_URL}/public/users/renew-token`
      const user = JSON.parse(storedUser)
      const currentRefreshToken = localStorage.getItem('refreshToken')
      const response = await axios.post(
        refreshEndpoint,
        { refreshToken: currentRefreshToken },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      if (response.data.accessToken) {
        const updatedUser = {
          ...user,
          token: response.data.accessToken,
          refreshToken: response.data.refreshToken || user.refreshToken,
        }
        localStorage.setItem('user', JSON.stringify(updatedUser))
        localStorage.setItem('token', response.data.accessToken)
        if (response.data.refreshToken) {
          localStorage.setItem('refreshToken', response.data.refreshToken)
        }
        setUser(updatedUser)
        return true
      }
    } catch (error: any) {
      console.error('Error refreshing token:', error)
      const status = error?.response?.status
      if (status === 401 || status === 403) {
        logout()
      }
    }
    return false
  }, [logout])

  const login = (userData: User) => {
    setUser(userData)
    localStorage.setItem('user', JSON.stringify(userData))
    localStorage.setItem('token', userData.token)
    localStorage.setItem('refreshToken', userData.refreshToken)
  }

  // Refresh token immediately on app load to validate session
  useEffect(() => {
    if (user) {
      refreshToken()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Proactive token refresh every 10 minutes
  useEffect(() => {
    if (user) {
      refreshTimerRef.current = setInterval(() => {
        refreshToken()
      }, REFRESH_INTERVAL_MS)
    }

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
    }
  }, [user, refreshToken])

  return <AuthContext.Provider value={{ user, login, logout, refreshToken }}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
